import { Key, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation } from 'react-router-dom'

import {
  BenefitDetailsResponse,
  BenefitParticipationStatus,
  BenefitUtilizationStatus,
  CompanyUserStatus,
  ProductIds,
  getBenefitDetails,
  getRenewalPlanPeriod,
  reInviteEmployees,
} from 'core'
import PagedList from 'core/application/commons/pagedList'
import getEmployees, { EmployeeResponse, EmployeesFilter } from 'core/application/companies/getEmployees'
import { BenefitDesignStatus } from 'core/domain/benefit/benefitDesignStatus'

import useTableFilter from 'common/hooks/useTableFilter'

import {
  Alert,
  Avatar,
  Button,
  Card,
  ConfirmButton,
  Dropdown,
  Notification,
  Table,
  TableFilter,
} from 'common/components'
import { formatYear, isTodayAfter, parseZoneFormat } from 'common/dateFormatters'
import { mapEnumObjectKeys, mapEnumObjectValues } from 'common/enumFormatters'
import { useCommandRequestV2, useLoginUserV2, useQueryRequestV2, useSharedState } from 'common/hooks'
import { ProductConfig } from 'common/providers/ProductProvider'
import { ROUTE_PATHS } from 'common/routePaths'

import menu from '../actionMenu'
import { inviteEmployees } from './inviteEmployees'

import './employeesTable.less'

type EmployeesTableProps = {
  reload?: boolean
  onLoaded?: () => void
  search?: string
  onTerminateClicked?: (user: EmployeeResponse) => void
  onOptInOptOutUserClicked?: (user: EmployeeResponse, type: string) => void
  onResendNoticeClicked?: (user: EmployeeResponse) => void
  onEditUsernameClicked?: (user: EmployeeResponse) => void
  planPeriodId: string
  hasActiveRunOutPeriod: boolean
  runOutPeriodYear?: number
}

const EmployeesTable = ({
  reload,
  onLoaded,
  search,
  onTerminateClicked,
  onOptInOptOutUserClicked,
  onResendNoticeClicked,
  onEditUsernameClicked,
  planPeriodId,
  hasActiveRunOutPeriod,
  runOutPeriodYear,
}: EmployeesTableProps) => {
  const history = useHistory()

  const { state } = useLocation<{ orderColumn: string }>()

  const INITIAL_FILTER: EmployeesFilter = {
    pageNumber: 1,
    pageSize: 10,
    orderColumn: state?.orderColumn ?? 'createdAt',
    orderDescending: true,
    eligible: true,
    onlyTerminated: false,
  }

  const { t: translate } = useTranslation()
  const { user } = useLoginUserV2()

  const { sendCommand: sendReInviteEmployees, isLoading: isReInviteEmployeesLoading } = useCommandRequestV2(() =>
    reInviteEmployees(),
  )

  const {
    data: pagedEmployees,
    isLoading: isEmployeesLoading,
    sendQuery: sendGetEmployeesQuery,
  } = useQueryRequestV2<PagedList<EmployeeResponse>>({
    initialState: null,
  })

  const {
    handleFilterInfoChange,
    dateFilterDropdown,
    filteredData,
    filteredInfo,
    sortingPaginationInfo,
    visibleFilter,
    handleVisibleFilter,
  } = useTableFilter(pagedEmployees?.items, 'eligible-employee-table', INITIAL_FILTER)

  const refreshTable = async () => {
    await sendGetEmployeesQuery(() => getEmployees(sortingPaginationInfo as EmployeesFilter))
    if (onLoaded) {
      onLoaded()
    }
  }

  const {
    data: planPeriodDetails,
    isLoading: isPlanPeriodDetailsLoading,
    sendQuery: sendGetPlanPeriodDetails,
  } = useQueryRequestV2<BenefitDetailsResponse>({})

  const { data: renewalPlanPeriod } = useQueryRequestV2({ initialState: null, queryFn: () => getRenewalPlanPeriod() })

  const renewalYear = Number(formatYear(renewalPlanPeriod?.startDate))

  useEffect(() => {
    ;(async () => {
      await refreshTable()
    })()
  }, [sortingPaginationInfo])

  useEffect(() => {
    if (search != undefined) {
      handleFilterInfoChange(filteredInfo, {
        ...sortingPaginationInfo,
        searchValue: search,
      })
    }
  }, [search])

  useEffect(() => {
    if (reload) {
      ;(async () => {
        await refreshTable()
      })()
    }
  }, [reload])

  useEffect(() => {
    if (planPeriodId) {
      ;(async () => {
        await sendGetPlanPeriodDetails(() => getBenefitDetails(planPeriodId))
      })()
    }
  }, [planPeriodId])

  const isIchra = user.productId === ProductIds.IchraId

  const columns: any[] = [
    {
      title: translate('admin-dashboard.manage-people.employee.first-name-label'),
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      isStatic: true,
      fixed: 'left',
      className: 't-eligible-employees-firstName',
      render: (text: string, record: EmployeeResponse) => (
        <span>
          <Link to={`${ROUTE_PATHS.MANAGE_PEOPLE}/view/${record.id}`} className="fullname_link">
            <div className="flex align-center" style={{ gap: '10px' }}>
              <Avatar style={{ backgroundColor: '#11D175' }}>{record.firstName[0]}</Avatar>
              {record.firstName}
            </div>
          </Link>
        </span>
      ),
    },
    {
      title: translate('admin-dashboard.manage-people.employee.last-name-label'),
      dataIndex: 'lastName',
      key: 'lastName',
      sorter: true,
      isStatic: true,
      fixed: 'left',
      sortDirections: ['descend', 'ascend'],
      className: 't-eligible-employees-lastName',
      render: (text: string, record: EmployeeResponse) => <span>{record.lastName}</span>,
    },
    ...(!isIchra
      ? [
          {
            title: 'Work email',
            dataIndex: 'workEmail',
            key: 'workEmail',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            className: 't-eligible-employees-workEmail',
            //render: (text: string, record: EmployeeResponse) => <span>{record.username ?? record.workEmail}</span>,
            render: (text: string, record: EmployeeResponse) => <span>{record.workEmail}</span>,
          },
          {
            title: translate('admin-dashboard.manage-people.employee.employment-status'),
            dataIndex: 'employmentStatus',
            key: 'employmentStatus',
            className: 't-eligible-employees-employmentStatus',
          },
        ]
      : []),

    {
      title: translate('admin-dashboard.manage-people.employee.pk-status'),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      className: 't-eligible-employees-status',
      filters: mapEnumObjectKeys(CompanyUserStatus)
        .filter((s) => s !== 'Terminated')
        .map((status, index) => ({
          text: status,
          value: status,
        })),
      filteredValue: filteredInfo.status || null,
      onFilterDropdownVisibleChange: handleVisibleFilter('status'),
      filterDropdownVisible: visibleFilter === 'status',
    },
    {
      title: translate('admin-dashboard.manage-people.employee.class'),
      dataIndex: 'employeeClass',
      key: 'employeeClass',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      className: 't-eligible-employees-employeeClass',
      // filters: mapEnumObjectValues(ClassTypeText).map((status, index) => ({
      //   text: status,
      //   value: status,
      // })),
      filters: planPeriodDetails?.productClasses?.map((classValue) => ({
        text: classValue.name,
        value: classValue.name,
      })),
      filteredValue: filteredInfo.employeeClass || null,
      onFilterDropdownVisibleChange: handleVisibleFilter('employeeClass'),
      filterDropdownVisible: visibleFilter === 'employeeClass',
    },
    ...(isIchra
      ? [
          {
            title: 'Current benefit participation status',
            dataIndex: 'benefitParticipationStatus',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            key: 'benefitParticipationStatus',
            className: 't-benefit-participation-status',
            render: (benefitParticipationStatus: number) =>
              mapEnumObjectValues(BenefitParticipationStatus)[benefitParticipationStatus],
            filters: mapEnumObjectValues(BenefitParticipationStatus).map((status, index) => ({
              text: status,
              value: index,
            })),
            filteredValue: filteredInfo.benefitParticipationStatus || null,
            onFilterDropdownVisibleChange: handleVisibleFilter('benefitParticipationStatus'),
            filterDropdownVisible: visibleFilter === 'benefitParticipationStatus',
          },
          ...(renewalPlanPeriod
            ? [
                {
                  title: renewalYear + ' Benefit participation status',
                  dataIndex: 'renewalBenefitParticipationDecision',
                  sorter: true,
                  sortDirections: ['descend', 'ascend'],
                  key: 'renewalBenefitParticipationDecision',
                  className: 't-renewal-participation-status',
                  render: (renewalParticipationStatus: number) =>
                    renewalParticipationStatus === null
                      ? 'Ineligible'
                      : mapEnumObjectValues(BenefitParticipationStatus)[renewalParticipationStatus],
                  filters: mapEnumObjectValues(BenefitParticipationStatus).map((status, index) => ({
                    text: status,
                    value: index,
                  })),
                  filteredValue: filteredInfo.renewalBenefitParticipationDecision || null,
                  onFilterDropdownVisibleChange: handleVisibleFilter('renewalBenefitParticipationDecision'),
                  filterDropdownVisible: renewalPlanPeriod && visibleFilter === 'renewalBenefitParticipationDecision',
                },
              ]
            : []),
          ...(hasActiveRunOutPeriod
            ? [
                {
                  title: runOutPeriodYear + ' Benefit participation status',
                  dataIndex: 'runOutBenefitParticipationDecision',
                  sorter: true,
                  sortDirections: ['descend', 'ascend'],
                  key: 'runOutBenefitParticipationDecision',
                  className: 't-runout-participation-status',
                  render: (runoutParticipationStatus: number) =>
                    runoutParticipationStatus === null
                      ? 'Ineligible'
                      : mapEnumObjectValues(BenefitParticipationStatus)[runoutParticipationStatus],
                  filters: mapEnumObjectValues(BenefitParticipationStatus).map((status, index) => ({
                    text: status,
                    value: index,
                  })),
                  filteredValue: filteredInfo.runOutBenefitParticipationDecision || null,
                  onFilterDropdownVisibleChange: handleVisibleFilter('runOutBenefitParticipationDecision'),
                  filterDropdownVisible:
                    hasActiveRunOutPeriod && visibleFilter === 'runOutBenefitParticipationDecision',
                },
              ]
            : []),
          {
            title: 'Benefit utilization status',
            dataIndex: 'benefitUtilizationStatus',
            key: 'benefitUtilizationStatus',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            className: 't-benefit-utilization-status',
            render: (benefitUtilizationStatus: number) =>
              benefitUtilizationStatus !== null &&
              mapEnumObjectValues(BenefitUtilizationStatus)[benefitUtilizationStatus],
            filters: mapEnumObjectValues(BenefitUtilizationStatus).map((status, index) => ({
              text: status,
              value: index,
            })),
            filteredValue: filteredInfo.benefitUtilizationStatus || null,
            onFilterDropdownVisibleChange: handleVisibleFilter('benefitUtilizationStatus'),
            filterDropdownVisible: visibleFilter === 'benefitUtilizationStatus',
          },
          {
            title: 'Ongoing attestation status',
            dataIndex: 'ongoingAttestationStatus',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            key: 'ongoingAttestationStatus',
            className: 't-ongoing-attestation-status',
            render: (ongoingAttestationStatus: boolean) => (ongoingAttestationStatus ? 'Yes' : 'No'),
            filters: [
              {
                text: 'Yes',
                value: true,
              },
              {
                text: 'No',
                value: false,
              },
            ],
            filteredValue: filteredInfo.ongoingAttestationStatus || null,
            onFilterDropdownVisibleChange: handleVisibleFilter('ongoingAttestationStatus'),
            filterDropdownVisible: visibleFilter === 'ongoingAttestationStatus',
          },
          {
            title: 'Opted-in to SMS?',
            dataIndex: 'smsOptedIn',
            key: 'smsOptedIn',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            className: 't-opted-in-to-sms',
            render: (smsOptedIn: boolean) => (smsOptedIn ? 'Yes' : 'No'),
            filters: [
              {
                text: 'Yes',
                value: true,
              },
              {
                text: 'No',
                value: false,
              },
            ],
            filteredValue: filteredInfo.smsOptedIn || null,
            onFilterDropdownVisibleChange: handleVisibleFilter('smsOptedIn'),
            filterDropdownVisible: visibleFilter === 'smsOptedIn',
          },
          {
            title: 'Notification date',
            dataIndex: 'notificationDate',
            key: 'notificationDate',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            className: 't-notification-date',
            render: (notificationDate: Date) => (notificationDate ? parseZoneFormat(notificationDate) : null),
            filterDropdown: dateFilterDropdown('notificationDate'),
            onFilterDropdownVisibleChange: handleVisibleFilter('notificationDate'),
            filterDropdownVisible: visibleFilter === 'notificationDate',
          },
          {
            title: 'Benefit eligibility date',
            dataIndex: 'benefitEligibilityDate',
            key: 'benefitEligibilityDate',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            className: 't-benefit-eligibility-date',
            render: (benefitEligibilityDate: Date) =>
              benefitEligibilityDate ? parseZoneFormat(benefitEligibilityDate) : null,
            filterDropdown: dateFilterDropdown('benefitEligibilityDate'),
            onFilterDropdownVisibleChange: handleVisibleFilter('benefitEligibilityDate'),
            filterDropdownVisible: visibleFilter === 'benefitEligibilityDate',
          },
        ]
      : [
          {
            title: translate('admin-dashboard.manage-people.employee.benefit-start-date'),
            dataIndex: 'benefitEligibilityDate',
            key: 'benefitEligibilityDate',
            className: 't-eligible-employees-benefitEligibilityDate',
            render: (benefitEligibilityDate: Date) =>
              benefitEligibilityDate ? parseZoneFormat(benefitEligibilityDate) : null,
          },
        ]),

    {
      title: translate('admin-dashboard.manage-people.employee.action'),
      key: 'action',
      fixed: 'right',
      isStatic: true,
      className: 't-eligible-employees-action',
      render: (text: string, record: EmployeeResponse) => (
        <>
          <Dropdown
            overlay={menu(
              record,
              inviteEmployees,
              translate,
              history,
              renewalYear,
              renewalPlanPeriod &&
                renewalPlanPeriod?.status === BenefitDesignStatus.Signed &&
                isTodayAfter(renewalPlanPeriod?.notificationDate),
              onTerminateClicked,
              onOptInOptOutUserClicked,
              onResendNoticeClicked,
              refreshTable,
              onEditUsernameClicked,
            )}
          >
            <Button>...</Button>
          </Dropdown>
        </>
      ),
    },
  ]

  const editableColumnsKeys = columns.filter((column) => !column.isStatic).map((column) => column.key)

  const [selectedColumns, setSelectedColumns] = useSharedState<Key[]>(
    `"eligible-employees-table"-selected-columns`,
    editableColumnsKeys,
    true,
  )

  const columnToSort: any = columns.find((c) => c.key === sortingPaginationInfo.orderColumn)
  if (columnToSort) {
    columnToSort.defaultSortOrder = sortingPaginationInfo.orderDescending ? 'descend' : 'ascend'
  }

  const onReInviteEmployees = async () => {
    const result = await sendReInviteEmployees({})
    if (result.completed) {
      Notification.success('Re-invite employees scheduled successfully.')
      refreshTable()
    }
  }

  const onTableFilterChanged = (pagination: any, filters: any, sorter: any) => {
    handleFilterInfoChange(filters, {
      ...sortingPaginationInfo,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field || 'createdAt',
      orderDescending: sorter.order !== 'ascend',
    })
  }

  return (
    <>
      <Card
        className="mb-16"
        title={
          <div className="flex flex__align_center w-100 flex__space_between">
            <h5 className="mb-0">{translate('admin-dashboard.manage-people.view-people.qsehra-title-eligible')}</h5>
            <div className="flex flex__align_center">
              {selectedColumns.length !== editableColumnsKeys.length && (
                <Alert type="warning" className="mr-8 h-32" message="Some columns are hidden" showIcon />
              )}
              <TableFilter
                selectedColumns={selectedColumns}
                columns={columns}
                onSelectedColumnsChange={(e) => setSelectedColumns(e)}
              />

              <ConfirmButton
                type="default"
                icon="fas fa-user-plus"
                disabled={isReInviteEmployeesLoading}
                loading={isReInviteEmployeesLoading}
                onConfirm={onReInviteEmployees}
                confirmModalContent={`Are you sure you want to re-send the ${
                  ProductConfig.get(user.productId).productName
                } Notice & PeopleKeep Welcome email to your previously Invited users?`}
                confirmOkButtonText={'Send'}
                className={'ml-8'}
              >
                Re-invite employees
              </ConfirmButton>
            </div>
          </div>
        }
      >
        <Table
          className={'t-eligible-employees'}
          dataSource={filteredData}
          onChange={onTableFilterChanged}
          loading={isEmployeesLoading}
          columns={columns.filter((column) => {
            return [...selectedColumns, ...columns.filter((c) => c.isStatic).map((c) => c.key)].includes(column.key)
          })}
          scroll={{ x: 'max-content' }}
          pagination={{
            total: pagedEmployees?.totalItemCount,
            pageSize: pagedEmployees?.pageSize,
            current: pagedEmployees?.pageNumber,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '40'],
          }}
        />
      </Card>
    </>
  )
}

export default EmployeesTable
